import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useApiClient } from '@/lib/api/hooks';

type ColorComboboxProps = {
  /** Full style string (`BrandName StyleName`) — gates the API call. */
  style: string;
  value: string;
  onPick: (colorName: string) => void;
  /**
   * Accessible name for the trigger. Needed wherever several of these sit in a list — the per-colorway
   * blank rows label each row with adjacent text only, which no screen reader ties to the control.
   * Mirrors `DesignCombobox`'s prop of the same name, which exists for the same reason.
   */
  ariaLabel?: string;
};

export function ColorCombobox({ style, value, onPick, ariaLabel }: ColorComboboxProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const disabled = style.trim().length === 0;

  function handlePick(colorName: string) {
    onPick(colorName);
    setOpen(false);
  }

  const trigger = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel}
      disabled={disabled}
      className="h-11 w-full justify-between text-base font-normal"
      onClick={() => setOpen(true)}
    >
      <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
        {value || (disabled ? 'Pick a style first' : 'Pick a color')}
      </span>
      <ChevronsUpDown className="ml-2 size-4 shrink-0 text-muted-foreground" />
    </Button>
  );

  return (
    <>
      {isMobile ? (
        <>
          {trigger}
          <CommandDialog open={open} onOpenChange={setOpen} title="Pick a color">
            <ColorPickerBody style={style} currentValue={value} onPick={handlePick} />
          </CommandDialog>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={true}>
              <ColorPickerBody style={style} currentValue={value} onPick={handlePick} />
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

function ColorPickerBody({
  style,
  currentValue,
  onPick,
}: {
  style: string;
  currentValue: string;
  onPick: (colorName: string) => void;
}) {
  const client = useApiClient();
  const trimmed = style.trim();

  const colors = useQuery({
    queryKey: ['style-colors', trimmed],
    enabled: trimmed.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error, response } = await client.GET('/garments/by-style/{style}', {
        params: { path: { style: trimmed } },
      });
      if (error) {
        throw {
          status: response.status,
          body: (error as { error?: { type?: string } })?.error,
        };
      }
      return data.data;
    },
  });

  return (
    <>
      <CommandInput placeholder="Filter by color name…" autoFocus />
      <CommandList className="max-h-[360px]">
        {colors.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading colors…
          </div>
        ) : colors.isError ? (
          <ErrorBody error={colors.error} style={trimmed} />
        ) : !colors.data || colors.data.colors.length === 0 ? (
          <CommandEmpty>No colors for this style.</CommandEmpty>
        ) : (
          <CommandGroup
            heading={`${colors.data.colors.length} color${colors.data.colors.length === 1 ? '' : 's'}`}
          >
            {colors.data.colors.map((c) => (
              <CommandItem
                key={c.colorName}
                value={`${c.colorName}|${c.colorFamily ?? ''}|${c.colorGroup ?? ''}`}
                onSelect={() => onPick(c.colorName)}
              >
                <ColorRow color={c} isActive={c.colorName === currentValue} />
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </>
  );
}

function ColorRow({
  color,
  isActive,
}: {
  color: {
    colorName: string;
    colorFamily?: string;
    colorGroup?: string;
    qtyInStock: number;
    effectivePrice: number;
  };
  isActive: boolean;
}) {
  const swatchColor = cssColorForName(color.colorName, color.colorGroup, color.colorFamily);
  return (
    <div className="flex flex-1 items-center gap-3">
      <span
        aria-hidden
        className="size-4 shrink-0 rounded-full border border-border"
        style={{ background: swatchColor }}
      />
      <span className="font-medium">{color.colorName}</span>
      {color.colorFamily && (
        <span className="text-xs text-muted-foreground">· {color.colorFamily}</span>
      )}
      <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
        {color.qtyInStock.toLocaleString()} stock
      </span>
      {isActive && <Check className="ml-2 size-4 text-primary" />}
    </div>
  );
}

function ErrorBody({ error, style }: { error: unknown; style: string }) {
  const typed = error as { status?: number; body?: { type?: string } } | undefined;
  const status = typed?.status;
  const type = typed?.body?.type;
  if (status === 404 || type === 'style_not_found') {
    return (
      <div className="space-y-2 px-4 py-6">
        <p className="text-sm font-medium">Style not found</p>
        <p className="text-xs text-muted-foreground">
          SS Activewear has no style matching "{style}". Re-pick from Search, or switch the garment
          source to <span className="font-medium">We provide</span> to enter a blank cost manually.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2 px-4 py-6">
      <p className="text-sm font-medium">Couldn't load colors</p>
      <p className="text-xs text-muted-foreground">
        SS Activewear is unavailable. Try again in a moment, or switch the garment source to{' '}
        <span className="font-medium">We provide</span>.
      </p>
    </div>
  );
}

/**
 * Best-effort CSS color for the swatch when SS doesn't provide a hex.
 * Matches common wholesale color names to a near-equivalent CSS value;
 * unknown names fall back to a neutral gray. Only visual — the actual
 * pricing uses SS's stored values.
 */
function cssColorForName(name: string, group?: string, family?: string): string {
  const candidates = [name, group, family]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map((s) => s.toLowerCase().trim());
  for (const c of candidates) {
    const hit = COLOR_HINTS.find(([pat]) => c === pat || c.includes(pat));
    if (hit) return hit[1];
  }
  return '#d4d4d8';
}

const COLOR_HINTS: Array<[string, string]> = [
  ['white', '#ffffff'],
  ['black', '#111111'],
  ['navy', '#1e2a49'],
  ['red', '#c4151c'],
  ['royal', '#1e3a8a'],
  ['blue', '#2563eb'],
  ['green', '#16a34a'],
  ['forest', '#14532d'],
  ['kelly', '#15803d'],
  ['yellow', '#facc15'],
  ['gold', '#ca8a04'],
  ['orange', '#ea580c'],
  ['pink', '#ec4899'],
  ['purple', '#7c3aed'],
  ['maroon', '#6b0f1a'],
  ['burgundy', '#6b0f1a'],
  ['charcoal', '#374151'],
  ['heather', '#9ca3af'],
  ['grey', '#9ca3af'],
  ['gray', '#9ca3af'],
  ['sand', '#d6cfbf'],
  ['tan', '#c8a06b'],
  ['khaki', '#b7a57a'],
  ['brown', '#78350f'],
  ['neutral', '#e4e4e7'],
  ['reds', '#c4151c'],
  ['blues', '#2563eb'],
  ['greens', '#16a34a'],
  ['yellows', '#facc15'],
  ['purples', '#7c3aed'],
  ['oranges', '#ea580c'],
  ['pinks', '#ec4899'],
  ['browns', '#78350f'],
  ['neutrals', '#e4e4e7'],
];
